import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, map, Observable, shareReplay, take } from 'rxjs';

import { environment } from 'src/environments/environment';

import { ProductCompanyYear } from '../models/features/product/ProductCompanyYear';
import { Trait } from '../models/Trait';

interface TraitProductState {
  traits: Trait[];
  products: ProductCompanyYear[];
  conditioners: any[];
  loaded: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TraitProductConditionerService {
  private state$ = new BehaviorSubject<TraitProductState>({
    traits: [],
    products: [],
    conditioners: [],
    loaded: false,
  });

  readonly data$ = this.state$.asObservable();

  constructor(private httpClient: HttpClient) {}

  loadAll(
    year: number,
    crop: string,
    cropId: number,
    forceReload: boolean = false,
  ): Observable<TraitProductState> {
    if (this.state$.getValue().loaded && !forceReload) {
      return this.data$.pipe(take(1));
    }

    return forkJoin({
      traits: this.traitLookup(year, cropId),
      products: this.productLookup(year, crop, ''),
      conditioners: this.lookupConditionerByProductAndYear('', year),
    }).pipe(
      map(({ traits, products, conditioners }) => {
        const filteredConditioners = conditioners.status === 200 ? conditioners.body : [];

        const filteredProducts = products.status === 200 ? products.body : [];

        const filteredTraits = traits.status === 200 ? traits.body : [];

        const newState: TraitProductState = {
          traits: filteredTraits,
          products: filteredProducts,
          conditioners: filteredConditioners,
          loaded: true,
        };

        this.state$.next(newState);
        return newState;
      }),
      shareReplay(1),
    );
  }

  public productLookup(
    year: number,
    crop: string,
    conditionerCode: string,
  ): Observable<HttpResponse<ProductCompanyYear[]>> {
    const params = new HttpParams()
      .set('year', year)
      .set('cropCode', crop)
      .set('conditionerCode', conditionerCode ? conditionerCode : '');
    return this.httpClient.get<ProductCompanyYear[]>(
      `${environment.apiUrl}/product/v1/lookupByCodeAndYear`,
      { params, observe: 'response' },
    );
  }

  public traitLookup(year: number, cropId: number): Observable<HttpResponse<Trait[]>> {
    const params = new HttpParams().set('year', year).set('cropId', cropId);
    return this.httpClient.get<Trait[]>(`${environment.apiUrl}/product/v1/lookupTraitByCrop`, {
      params,
      observe: 'response',
    });
  }

  public lookupConditionerByProductAndYear(
    productCode: string,
    year: number,
  ): Observable<HttpResponse<any[]>> {
    const params = new HttpParams().set('productCode', productCode).set('year', year);
    return this.httpClient.get<any[]>(
      `${environment.apiUrl}/conditioner/v1/lookupConditionerByProductAndYear`,
      { params, observe: 'response' },
    );
  }
}
